import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import type { CallDto } from '@shared/services/apiService/apiService';
import { formatCallType, formatInteractionResult } from '@pages/Admin/Campaign/ui/utils';
import { formatLocalDateTime } from '@features/CallDetail';
import { CopyButton } from './CopyButton';

interface TechnicalDetailsProps {
  call: CallDto;
}

const DetailRow: React.FC<{ label: string; value?: string | null; copyValue?: string | null }> = ({
  label,
  value,
  copyValue,
}) => {
  const { t } = useTranslation();
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Typography variant="body2" color="text.secondary" minWidth={{ sm: 180 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
          {value || t('campaignPage.unavailable')}
        </Typography>
        {copyValue ? (
          <CopyButton
            value={copyValue}
            label={t('campaignPage.callDetailHeader.copyValue')}
            copiedLabel={t('campaignPage.callDetailHeader.copied')}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};

export const TechnicalDetails: React.FC<TechnicalDetailsProps> = ({ call }) => {
  const { t } = useTranslation();
  const rows = [
    [t('campaignPage.technicalDetails.callId'), call.id, call.id],
    [t('campaignPage.technicalDetails.roomName'), call.roomName, call.roomName],
    [t('campaignPage.technicalDetails.customerId'), call.customerId, call.customerId],
    [t('campaignPage.technicalDetails.agentId'), call.agentId, call.agentId],
    [t('campaignPage.technicalDetails.squadId'), call.squadId, call.squadId],
    [t('campaignPage.technicalDetails.companyId'), call.companyId, call.companyId],
    [t('campaignPage.technicalDetails.type'), formatCallType(call.type)],
    [t('campaignPage.technicalDetails.status'), formatInteractionResult(call.status)],
    [
      t('campaignPage.technicalDetails.endedReason'),
      call.endedReason ? formatInteractionResult(call.endedReason) : null,
    ],
    [t('campaignPage.technicalDetails.createdAt'), formatLocalDateTime(call.createdAt)],
    [t('campaignPage.technicalDetails.updatedAt'), formatLocalDateTime(call.updatedAt)],
    [t('campaignPage.technicalDetails.startedAt'), formatLocalDateTime(call.startedAt)],
    [t('campaignPage.technicalDetails.endedAt'), formatLocalDateTime(call.endedAt)],
    [
      t('campaignPage.technicalDetails.recording'),
      call.recordingUrl ? t('campaignPage.callDetailHeader.available') : null,
    ],
    [t('campaignPage.technicalDetails.analysisStatus'), call.analysisStatus],
  ] as const;

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>{t('campaignPage.technicalDetails.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box component={Stack} spacing={1.25}>
          {rows.map(([label, value, copyValue]) => (
            <DetailRow key={label} label={label} value={value} copyValue={copyValue} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
